import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CabinBookingsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cabin: '',
    check_in: '',
    check_out: '',
    num_guests: 1,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const cabins = [
    {
      name: 'Halibut House',
      emoji: '🐟',
      description: 'Spacious and cozy floating cabin with stunning water views. Perfect for families!',
      features: ['Full Kitchen', 'Full bathroom with shower and hot water', 'Linens Provided', 'Sleeps 4-6'],
      video: null,
      photos: [
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/6eu8n3y8_IMG_7991.PNG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/j5abnjtx_IMG_7992.PNG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/puit57mm_IMG_7993.PNG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/4n45sd76_IMG_7994.PNG',
      ],
    },
    {
      name: 'Seabass Suite',
      emoji: '🐠',
      description: 'Romantic getaway with panoramic ocean views. Ideal for couples and small groups.',
      features: ['Full Kitchen', 'Full bathroom with shower and hot water', 'Linens Provided', 'Sleeps 2-4'],
      video: 'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/diael4wp_Cabin%203.MOV',
      posterTime: 13,
    },
    {
      name: 'Yellow House',
      emoji: '🏡',
      description: 'Bright and cheerful cabin with modern amenities. Great for adventure seekers!',
      features: ['Full Kitchen', 'Full bathroom with shower and hot water', 'Linens Provided', 'Sleeps 4-6'],
      video: 'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/sx8xqw9p_Cabin%201.MOV',
      posterTime: 17,
    },
    {
      name: 'La Perlita',
      emoji: '💎',
      description: 'Our newest floating cabin coming soon! A pearl of an experience awaits.',
      features: ['Full Kitchen', 'Linens Provided', 'Sleeps TBD'],
      video: null,
      comingSoon: true,
      image: 'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/u1igskw8_IMG_7941.jpeg',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/cabin-booking`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        cabin: '',
        check_in: '',
        check_out: '',
        num_guests: 1,
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Component to handle video with custom poster
  const VideoWithPoster = ({ src, posterTime }) => {
    const videoRef = useRef(null);
    const [posterUrl, setPosterUrl] = useState('');

    useEffect(() => {
      if (videoRef.current && posterTime) {
        const video = videoRef.current;
        
        const captureFrame = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          setPosterUrl(canvas.toDataURL('image/jpeg', 0.8));
        };

        const handleLoadedMetadata = () => {
          video.currentTime = posterTime;
        };

        const handleSeeked = () => {
          captureFrame();
          video.currentTime = 0;
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('seeked', handleSeeked);

        return () => {
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          video.removeEventListener('seeked', handleSeeked);
        };
      }
    }, [posterTime]);

    return (
      <video
        ref={videoRef}
        controls
        className="w-full h-80 object-cover"
        poster={posterUrl}
        preload="metadata"
      >
        <source src={src} type="video/mp4" />
        <source src={src} type="video/quicktime" />
      </video>
    );
  };

  return (
    <div className="pt-20">
      {/* Hero Video Section */}
      <section className="relative h-[80vh] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="cabins-hero-video"
        >
          <source
            src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/3a5wlh5x_reel-1%20%28no%20film%29.MOV"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#FFB6C1]/40 via-transparent to-[#FF69B4]/40"></div>
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl brand-script text-white mb-6 drop-shadow-2xl animate-fade-in-up" data-testid="cabins-hero-title">
              Floating Cabins 🏖️
            </h1>
            <p className="text-xl md:text-2xl text-white mb-8 drop-shadow-lg">
              Your own private island paradise! Stay in our unique floating cabins on Strawberry Island.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#booking-form"
                className="bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
              >
                Book Your Stay 🏠
              </a>
              <a
                href="mailto:joel@fishingtofino.net?subject=Cabin Inquiry"
                className="bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform duration-300 shadow-lg"
              >
                Ask Us Anything 💬
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6" style={{ color: '#20B2AA' }}>
            A Getaway Like No Other! 🌟
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-4">
            Located on Strawberry Island in Tofino, BC, our floating cabin rentals offer 
            a truly unique experience. Just 30 seconds from downtown Tofino by water taxi, 
            yet a world away from everyday life!
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Each cabin comes with a full kitchen and all linens. 
            Pearl, your friendly host, will pick you up via water taxi – just call an hour before!
          </p>
          <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#FF69B4]/10 rounded-xl p-4 inline-block">
            <p className="text-lg font-semibold" style={{ color: '#FF1493' }}>
              ⭐ Ask about our special off-season discounted rates for winter storm watching and peaceful retreats!
            </p>
          </div>
        </div>
      </section>

      {/* Cabins Showcase */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#FF69B4' }}>
            Choose Your Cabin 🏡
          </h2>
          <div className="space-y-16">
            {cabins.map((cabin, index) => (
              <div
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`cabin-${cabin.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {cabin.comingSoon ? (
                      <div className="relative w-full h-80">
                        <img
                          src={cabin.image}
                          alt={cabin.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40 flex items-center justify-center">
                          <div className="text-center text-white">
                            <div className="text-8xl mb-4">{cabin.emoji}</div>
                            <p className="text-4xl font-bold brand-script mb-2">Coming Soon</p>
                            <p className="text-xl">Opening 2025</p>
                          </div>
                        </div>
                      </div>
                    ) : cabin.photos ? (
                      <div className="w-full h-80 grid grid-cols-2 grid-rows-2 gap-1 p-1 bg-gray-100">
                        {cabin.photos.map((photo, photoIndex) => (
                          <img
                            key={photoIndex}
                            src={photo}
                            alt={`${cabin.name} ${photoIndex + 1}`}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ))}
                      </div>
                    ) : cabin.video ? (
                      <VideoWithPoster src={cabin.video} posterTime={cabin.posterTime} />
                    ) : (
                      <div className="w-full h-80 bg-gradient-to-br from-[#20B2AA] to-[#5DADE2] flex items-center justify-center">
                        <div className="text-center text-white">
                          <div className="text-8xl mb-4">{cabin.emoji}</div>
                          <p className="text-xl font-semibold">Photos Coming Soon!</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <h3 className="text-4xl font-bold mb-4" style={{ color: '#20B2AA' }}>
                    {cabin.emoji} {cabin.name}
                  </h3>
                  <p className="text-xl text-gray-700 mb-6">{cabin.description}</p>
                  <div className="bg-gradient-to-br from-[#FF69B4]/10 to-[#FFB6C1]/10 rounded-xl p-6 mb-4">
                    <h4 className="text-xl font-bold mb-4" style={{ color: '#FF1493' }}>
                      ✨ Features & Amenities
                    </h4>
                    <ul className="space-y-2">
                      {cabin.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <span className="text-2xl mr-3">✅</span>
                          <span className="text-lg">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {!cabin.comingSoon && (
                    <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#5DADE2]/10 rounded-xl p-4">
                      <p className="text-lg font-semibold text-center" style={{ color: '#20B2AA' }}>
                        💰 <a href={`mailto:joel@fishingtofino.net?subject=Pricing Inquiry for ${cabin.name}`} className="hover:underline">Inquire for Pricing</a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Winter Storm Watching */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#5DADE2]/20 to-[#20B2AA]/20 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#20B2AA' }}>
                ⛈️ Winter Storm Watching Season!
              </h2>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-6">
                Experience the raw power and beauty of Pacific storms from the comfort and safety 
                of your floating cabin! November through March brings dramatic waves, spectacular 
                sunsets, and unforgettable memories.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-5xl mb-4">🌊</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#FF69B4' }}>
                  Epic Waves
                </h3>
                <p className="text-gray-700">
                  Watch massive waves crash against the rocky shores from your warm, cozy cabin
                </p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center shadow-lg">
                <div className="text-5xl mb-4">📸</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#FF69B4' }}>
                  Photo Ops
                </h3>
                <p className="text-gray-700">
                  Capture breathtaking storm clouds, dramatic lighting, and wild coastal scenery
                </p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg text-gray-700 mb-6">
                💸 <strong>Special off-season rates available!</strong> Ask us about winter discounts 
                for storm watching adventures and peaceful winter retreats.
              </p>
              <a
                href="mailto:joel@fishingtofino.net?subject=Winter Storm Watching Inquiry"
                className="inline-block bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white px-10 py-4 rounded-full text-xl font-semibold hover:scale-105 transition-transform shadow-lg"
              >
                Book Your Winter Escape ⛈️
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-16 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-4xl font-bold text-center mb-8" style={{ color: '#FF69B4' }}>
              Book Your Cabin Stay 🏡
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-lg font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-lg font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-lg font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  placeholder="250-123-4567"
                />
              </div>
              <div>
                <label htmlFor="cabin" className="block text-lg font-medium text-gray-700 mb-2">
                  Cabin Selection *
                </label>
                <select
                  id="cabin"
                  name="cabin"
                  required
                  value={formData.cabin}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                >
                  <option value="">Select a cabin...</option>
                  <option value="Halibut House">🐟 Halibut House</option>
                  <option value="Seabass Suite">🐠 Seabass Suite</option>
                  <option value="Yellow House">🏡 Yellow House</option>
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="check_in" className="block text-lg font-medium text-gray-700 mb-2">
                    Check-In Date *
                  </label>
                  <input
                    type="date"
                    id="check_in"
                    name="check_in"
                    required
                    value={formData.check_in}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="check_out" className="block text-lg font-medium text-gray-700 mb-2">
                    Check-Out Date *
                  </label>
                  <input
                    type="date"
                    id="check_out"
                    name="check_out"
                    required
                    value={formData.check_out}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="num_guests" className="block text-lg font-medium text-gray-700 mb-2">
                  Number of Guests *
                </label>
                <input
                  type="number"
                  id="num_guests"
                  name="num_guests"
                  required
                  min="1"
                  max="10"
                  value={formData.num_guests}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-lg font-medium text-gray-700 mb-2">
                  Special Requests or Questions
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none transition-colors"
                  placeholder="Let us know if you have any special requests..."
                />
              </div>
              {submitStatus === 'success' && (
                <div className="bg-green-100 border-2 border-green-400 text-green-700 px-4 py-3 rounded-lg">
                  ✅ Thank you! Your booking inquiry has been submitted. We'll contact you shortly!
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-lg">
                  ❌ Something went wrong. Please try again or email us directly at joel@fishingtofino.net
                </div>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#FF69B4] to-[#FFB6C1] text-white px-8 py-4 rounded-full text-xl font-semibold hover:scale-105 transition-transform shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Booking Inquiry 🎉'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#20B2AA] to-[#FF69B4] text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Experience Island Life? 🌴
          </h2>
          <p className="text-xl mb-8">
            Book your floating cabin getaway today and create memories that will last a lifetime!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:joel@fishingtofino.net"
              className="bg-white text-[#20B2AA] px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
              data-testid="contact-cta-btn"
            >
              Email Us 📧
            </a>
            <a
              href="tel:+12503170600"
              className="bg-white text-[#FF69B4] px-8 py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform shadow-lg"
            >
              Call Now 📞
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CabinBookingsPage;